import { oc } from "@orpc/contract";
import { z } from "zod";

// --- Shared schemas ---

const webhookSubscriptionSchema = z.object({
  mailboxUpn: z.string(),
  subscriptionId: z.string(),
  expirationDateTime: z.string(),
  resource: z.string().optional(),
  clientState: z.string().nullish(),
  applicationId: z.string().optional(),
  changeType: z.string().optional(),
});

const webhookStatusSchema = z.object({
  isActive: z.boolean(),
  subscriptions: z.array(webhookSubscriptionSchema),
  lastChecked: z.date(),
});

const mailboxPermissionSchema = z.enum(["read", "write"]);

const mailboxSchema = z.object({
  id: z.uuid(),
  upn: z.string(),
  name: z.string().nullable(),
  enabled: z.boolean(),
  permission: mailboxPermissionSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});

const emailIngestConfigSummarySchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  enabled: z.boolean(),
  tenantId: z.string().nullable(),
  clientId: z.string().nullable(),
  hasClientSecret: z.boolean(),
  clientSecretExpiresAt: z.string().nullable(),
  mailboxes: z.array(mailboxSchema),
  webhookStatus: webhookStatusSchema.optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

const webhookSubscriptionFailureSchema = z.object({
  mailboxUpn: z.string(),
  error: z.string(),
});

// --- Contracts ---

const listContract = oc.output(z.array(emailIngestConfigSummarySchema)).route({
  method: "GET",
  summary: "List all email ingest configs for the organization",
});

const getContract = oc
  .input(z.object({ appRegistrationId: z.uuid() }))
  .output(emailIngestConfigSummarySchema)
  .route({
    method: "GET",
    summary: "Get a single email ingest config with webhook status",
  });

const createContract = oc
  .input(
    z.object({
      name: z.string().trim().min(1).max(255),
      tenantId: z.string().trim().min(1),
      clientId: z.string().trim().min(1),
      clientSecret: z.string().trim().min(1),
      clientSecretExpiresInDays: z.number().int().positive().nullish(),
    }),
  )
  .output(emailIngestConfigSummarySchema)
  .route({
    summary: "Create a new email ingest config",
  });

const updateContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      name: z.string().trim().min(1).max(255).optional(),
      enabled: z.boolean().optional(),
      // Credential rotation — Azure's "Secret ID" is not accepted here; callers
      // must pass the secret *Value* shown once when the client secret is created.
      tenantId: z.string().trim().min(1).optional(),
      clientId: z.string().trim().min(1).optional(),
      clientSecret: z.string().trim().min(1).optional(),
      clientSecretExpiresInDays: z.number().int().positive().nullish(),
    }),
  )
  .output(
    emailIngestConfigSummarySchema.extend({
      /**
       * Present when credential rotation / enable attempted webhook
       * re-subscribe and one or more mailboxes failed. Credentials are still
       * persisted; callers should surface this instead of treating the update
       * as fully healthy.
       */
      subscriptionFailures: z
        .array(webhookSubscriptionFailureSchema)
        .optional(),
    }),
  )
  .route({
    summary:
      "Update an email ingest config (name, enabled, and/or Azure credentials)",
  });

const deleteContract = oc
  .input(z.object({ appRegistrationId: z.uuid() }))
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Delete an email ingest config and its webhooks",
  });

const toggleUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      mailboxId: z.uuid(),
      enabled: z.boolean(),
    }),
  )
  .output(mailboxSchema)
  .route({
    summary: "Toggle a mailbox UPN on/off",
  });

const addUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      upn: z.string().email(),
      name: z.string().trim().nullish(),
      permission: mailboxPermissionSchema,
    }),
  )
  .output(mailboxSchema)
  .route({
    summary: "Add a mailbox UPN to a config",
  });

const updateUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      mailboxId: z.uuid(),
      upn: z.string().email().optional(),
      name: z.string().trim().nullish(),
    }),
  )
  .output(mailboxSchema)
  .route({
    summary: "Update a mailbox's UPN and/or name",
  });

const removeUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      mailboxId: z.uuid(),
    }),
  )
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Remove a mailbox UPN from a config",
  });

const refreshWebhooksContract = oc
  .input(z.object({ appRegistrationId: z.uuid() }))
  .output(webhookStatusSchema)
  .route({
    summary: "Force refresh all webhook subscriptions for a config",
  });

const refreshUpnContract = oc
  .input(z.object({ appRegistrationId: z.uuid(), mailboxId: z.uuid() }))
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Refresh the webhook subscription for a single mailbox UPN",
  });

const lookbackRequeueMailboxSchema = z.object({
  mailboxUpn: z.string(),
  emailsFound: z.number().int().nonnegative(),
  emailsQueued: z.number().int().nonnegative(),
  emailsSkipped: z.number().int().nonnegative(),
  partialChunks: z.number().int().nonnegative(),
  errors: z.array(z.string()),
});

const requeueMissedEmailsContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      /** Hours to look back from now (exclusive of the trailing exclude window). */
      lookbackHours: z.number().positive().max(168),
    }),
  )
  .output(
    z.object({
      status: z.enum(["completed", "completed_with_errors", "busy", "noop"]),
      organizationId: z.uuid(),
      appRegistrationId: z.uuid(),
      lookbackHours: z.number(),
      chunkHours: z.number(),
      chunksTotal: z.number().int().nonnegative(),
      chunksCompleted: z.number().int().nonnegative(),
      emailsFound: z.number().int().nonnegative(),
      /** Missed emails published back onto ms-graph-email-ingest. */
      emailsQueued: z.number().int().nonnegative(),
      /**
       * Same as emailsQueued — explicit alias for UI/ops copy
       * ("N missed emails were requeued").
       */
      missedEmailsRequeued: z.number().int().nonnegative(),
      emailsSkipped: z.number().int().nonnegative(),
      partialChunks: z.number().int().nonnegative(),
      durationMs: z.number().int().nonnegative(),
      mailboxes: z.array(lookbackRequeueMailboxSchema),
      message: z.string().optional(),
    }),
  )
  .route({
    summary:
      "Requeue missed emails for a config over a lookback window (chunked, memory-bounded)",
  });

export const emailIngestConfigsContracts = {
  list: listContract,
  get: getContract,
  create: createContract,
  update: updateContract,
  delete: deleteContract,
  toggleUpn: toggleUpnContract,
  addUpn: addUpnContract,
  updateUpn: updateUpnContract,
  removeUpn: removeUpnContract,
  refreshWebhooks: refreshWebhooksContract,
  refreshUpn: refreshUpnContract,
  requeueMissedEmails: requeueMissedEmailsContract,
};

export type EmailIngestConfigsContracts = typeof emailIngestConfigsContracts;
